/**
 * Where inside this interface a person was going when they were asked to sign in.
 *
 * The sign-in screen is reached with `?to=<path>`, and the path is chosen by whoever wrote the link.
 * It is therefore accepted only as a path of this application: one leading slash, no scheme, no host,
 * and not the service's own `/v1/` surface, which the sign-in screen resumes by other means. Anything
 * else resumes nowhere, and the person lands on the overview.
 */
export class Resume {
	/** Set by the service's hand-off when Accounts could not answer it: the hand-off is resumed once it does */
	static readonly OUTAGE = 'resume';
	/** `?mode=window`: the sign-in runs in the window a page's session opened, and ends on the landing */
	static readonly WINDOW = 'window';
	/** The page that ends a sign-in in that window, running `@beyond-js/ui/session/landed` */
	static readonly LANDING = '/session/landed';

	/**
	 * Where a sign-in in the session's window goes once someone is signed in: the landing, which tells
	 * the page that opened the window and closes it. Undefined for any other sign-in.
	 */
	static landed(parameters: URLSearchParams): string | undefined {
		if (parameters.get('mode') !== Resume.WINDOW) return undefined;
		return `${Resume.LANDING}?session=renewed&product=accounts`;
	}

	/**
	 * @returns The path with its query, or undefined when the value is not a path of this interface
	 */
	static path(value: string | null | undefined): string | undefined {
		if (!value || !value.startsWith('/') || value.startsWith('//') || value.includes('\\')) return undefined;
		if (value.startsWith('/v1/') || value.startsWith('/signin')) return undefined;
		try {
			// Resolved against a placeholder: a value that changes the origin was never a path
			const base = 'http://interface.invalid';
			const url = new URL(value, base);
			if (url.origin !== base) return undefined;
			return `${url.pathname}${url.search}`;
		} catch {
			return undefined;
		}
	}

	/**
	 * The service's own hand-off a person was on their way to when asked to sign in (`?to=/v1/authorize?…`),
	 * as an address of `authority`; anything else is not followed
	 */
	static handoff(value: string | null | undefined, authority: string): string | undefined {
		if (!value || !value.startsWith('/v1/authorize?')) return undefined;
		return `${authority}${value}`;
	}

	/**
	 * Where a person goes once signed in, when more than one way on is known: the product that sent
	 * them with this very request (`?product=&return=`, validated), then the hand-off they were
	 * interrupted on (`?to=/v1/authorize?…`), then a product kept from earlier in this tab's visit.
	 * An earlier trip never overrides where this sign-in was going.
	 *
	 * @param specs `requested`: whether this request named a product to return to; `sender`: the
	 * validated way back (this request's, or the kept one); `to`: the `to` parameter as it arrived
	 * @returns An address to replace the page with, or undefined to stay in this interface
	 */
	static next({ requested, sender, to, authority }: { requested: boolean; sender?: { destination: string }; to: string | null; authority: string }): string | undefined {
		if (requested && sender) return sender.destination;
		return Resume.handoff(to, authority) ?? sender?.destination;
	}
}
